'use client';

import { faLocationDot, faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import { faCircleXmark } from '@fortawesome/pro-solid-svg-icons';

import { InitialLocationSuggestions } from './initial-location-suggestions.component';
import { LocationSearchResults } from './location-search-results.component';
import { RecentSearchedLocations } from './recent-searched-locations.component';
import { RegisterAddress } from './register-address.component';

import { Button } from '@/components/ui/button/button.component';
import { Input } from '@/components/ui/input/input.component';
import Banner from '@/components/ui/banner.component';
import { AutocompletePrediction, Place } from '@/types/google-maps.types';
import { Typography } from '@/components/ui/typography/typography.component';

const MIN_SEARCH_LENGTH = 3;

interface LocationModalContentProps {
  searchDisplayValue: string;
  searchResults: AutocompletePrediction[];
  isLoading: boolean;
  shouldShowEmptyResultsMessage: boolean;
  deniedGeolocation: boolean;
  isLoggedIn: boolean;
  recentSearchesCount: number;
  onSearchInput: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onUseMyLocation: () => Promise<void>;
  onLocationClick: (prediction: AutocompletePrediction) => Promise<void>;
  onSuggestionClick: (place: Place) => Promise<void>;
  onResetSearchResults: () => void;
}

export const LocationModalContent = ({
  searchDisplayValue,
  searchResults,
  isLoading,
  shouldShowEmptyResultsMessage,
  deniedGeolocation,
  isLoggedIn,
  recentSearchesCount,
  onSearchInput,
  onUseMyLocation,
  onLocationClick,
  onSuggestionClick,
  onResetSearchResults,
}: LocationModalContentProps) => {
  const hasPredictions = searchResults.length > 0;
  const hasRecentSearches = recentSearchesCount > 0;

  const shouldShowRecentSearched = hasRecentSearches && !searchDisplayValue;
  const shouldShowRegisterAddress = !searchDisplayValue && isLoggedIn;
  const shouldShowLocationSearchResults = !shouldShowRecentSearched && hasPredictions;
  const shouldShowInitialSuggestions
    = !shouldShowRecentSearched
      && !searchDisplayValue
      && !hasPredictions
      && !isLoggedIn;
  const shouldShowEmptyResults
    = !hasPredictions
      && searchDisplayValue.length >= MIN_SEARCH_LENGTH
      && shouldShowEmptyResultsMessage;

  const containerClassName = 'flex flex-col gap-4 pt-4 px-5 pb-6';

  return (
    <div
      className={containerClassName}
      data-testid={DataTestIdEnum.LOCATION_SELECTION_MODAL}
    >
      <div className="flex flex-col gap-4">
        {deniedGeolocation && (
          <Banner icon={faCircleXmark} iconSize="lg" variant="danger">
            Não foi possível localizar sua cidade. Use o campo abaixo.
          </Banner>
        )}

        <div className="flex flex-col items-start gap-3">
          <Input
            key="location-search-input"
            data-testid={DataTestIdEnum.HOME_LOCATION_SEARCH_INPUT}
            value={searchDisplayValue}
            startIcon={faMagnifyingGlass}
            placeholder="Busque pela cidade, bairro ou rua"
            onChange={onSearchInput}
          />
          {searchDisplayValue.length > 0
            && searchDisplayValue.length < MIN_SEARCH_LENGTH && (
            <span className="text-sm text-gray-500">
              Digite pelo menos
              {' '}
              {MIN_SEARCH_LENGTH}
              {' '}
              caracteres para buscar.
            </span>
          )}
        </div>
      </div>

      {isLoading
        ? (
            <div className="loading loading-lg my-4"></div>
          )
        : (
            <div className="flex flex-col gap-5">
              {shouldShowRegisterAddress && (
                <RegisterAddress handleLocationClick={onLocationClick} />
              )}

              <div className="flex flex-col items-start justify-center gap-4 border-b border-gray-300 pb-4">
                {shouldShowRecentSearched && (
                  <RecentSearchedLocations
                    handleLocationClick={onLocationClick}
                    callback={onResetSearchResults}
                  />
                )}

                {shouldShowLocationSearchResults && (
                  <LocationSearchResults
                    searchResults={searchResults}
                    handleLocationClick={onLocationClick}
                  />
                )}

                {shouldShowInitialSuggestions && (
                  <InitialLocationSuggestions
                    handleLocationClick={onSuggestionClick}
                  />
                )}

                {shouldShowEmptyResults && (
                  <Typography>
                    Sem resultados para essa busca.
                  </Typography>
                )}
              </div>

              {!deniedGeolocation && (
                <Button
                  variant="text"
                  startIcon={faLocationDot}
                  className="text-lg"
                  onClick={onUseMyLocation}
                >
                  Usar minha localização
                </Button>
              )}
            </div>
          )}
    </div>
  );
};
